/**
 * The **Create** tab (captures `01-centre-open.png`, `10-gallery-*.png`).
 *
 * A 274 px category rail on the left, a search field and the shelves on the right: "Start with
 * the basics" (a **Create from scratch** tile plus three template cards) and then one shelf per
 * section, each with its blurb. Searching replaces the shelves with "<n> results found" and the
 * matching cards, grouped by section — monday's own behaviour in `10-gallery-setstatus.png`.
 *
 * The rail's *AI-powered*, *2-way sync*, *Sync* and *Connected & mirror columns* entries are
 * absent, not disabled: this product has no feature behind them (M1-B1 §3.2).
 */
import { Icon, Search, icons } from '@elizmonday/ui';
import { useMemo, useState, type RefObject } from 'react';
import { TEMPLATE_SECTIONS, type AutomationTemplate, type TemplateSection } from '@elizmonday/shared';
import type { TemplateGalleryView } from './types';

export type GalleryCategory = 'Explore' | TemplateSection | 'Created by';

/**
 * The rail, derived from the section list rather than retyped — M6-A4 added **Sync** and
 * **Integrations** (`specs/M6-A4-integrations.md` §7), and a hard-coded copy of the list here
 * is exactly how a new section reaches the gallery's shelves but never its rail.
 */
const RAIL_CATEGORIES: readonly GalleryCategory[] = ['Explore', ...TEMPLATE_SECTIONS];

export interface CreateGalleryProps {
  gallery: TemplateGalleryView;
  accountName: string;
  installedCount: number;
  onUseTemplate(template: AutomationTemplate): void;
  onCreateFromScratch(): void;
  onShowInstalled(): void;
  /** The anchor the "choose a trigger" popover hangs from (the tile itself). */
  scratchRef?: RefObject<HTMLButtonElement | null>;
}

export function CreateGallery({
  gallery,
  accountName,
  installedCount,
  onUseTemplate,
  onCreateFromScratch,
  onShowInstalled,
  scratchRef,
}: CreateGalleryProps) {
  const [category, setCategory] = useState<GalleryCategory>('Explore');
  const [query, setQuery] = useState('');
  const needle = query.trim().toLowerCase();

  const shelves = useMemo(() => {
    const sections = gallery.sections.filter((shelf) => category === 'Explore' || shelf.section === category);
    if (needle.length === 0) return sections;
    return sections
      .map((shelf) => ({ ...shelf, templates: shelf.templates.filter((t) => t.title.toLowerCase().includes(needle)) }))
      .filter((shelf) => shelf.templates.length > 0);
  }, [gallery.sections, category, needle]);

  const resultCount = shelves.reduce((total, shelf) => total + shelf.templates.length, 0);
  const basics = gallery.basics
    .map((recipeId) => gallery.templates.find((template) => template.recipeId === recipeId))
    .filter((template): template is AutomationTemplate => template !== undefined);

  return (
    <div className="ez-automation-create" data-testid="automations-create">
      <nav className="ez-automation-rail" aria-label="Categories">
        <h3 className="ez-automation-rail__title">Categories</h3>
        {RAIL_CATEGORIES.map((entry) => (
          <button
            key={entry}
            type="button"
            className="ez-automation-rail__item"
            aria-current={category === entry}
            data-testid={`gallery-category-${entry.toLowerCase()}`}
            onClick={() => setCategory(entry)}
          >
            <Icon icon={RAIL_ICONS[entry]} size={16} ignoreFocusStyle />
            {entry}
          </button>
        ))}
        <button
          type="button"
          className="ez-automation-rail__item"
          data-testid="gallery-category-account"
          onClick={onShowInstalled}
        >
          <Icon icon={icons.workspace} size={16} ignoreFocusStyle />
          {`Created by ${accountName}`}
          <span className="ez-automation-rail__count">{installedCount}</span>
        </button>
      </nav>

      <div className="ez-automation-create__body">
        <div className="ez-automation-create__search">
          <Search
            value={query}
            onChange={setQuery}
            placeholder="Search"
            aria-label="Search templates"
            data-testid="gallery-search"
          />
        </div>

        {needle.length > 0 ? (
          <p className="ez-automation-create__results" data-testid="gallery-results">
            {`${String(resultCount)} results found`}
          </p>
        ) : (
          <section className="ez-automation-shelf" data-testid="gallery-basics">
            <h3 className="ez-automation-shelf__title">Start with the basics</h3>
            <div className="ez-automation-shelf__cards">
              <button
                ref={scratchRef}
                type="button"
                className="ez-automation-scratch"
                data-testid="gallery-create-from-scratch"
                onClick={onCreateFromScratch}
              >
                <Icon icon={icons.add} size={24} ignoreFocusStyle />
                <span className="ez-automation-scratch__title">Create from scratch</span>
              </button>
              {basics.map((template) => (
                <TemplateCard key={template.recipeId} template={template} onUse={onUseTemplate} />
              ))}
            </div>
          </section>
        )}

        {shelves.map((shelf) => (
          <section key={shelf.section} className="ez-automation-shelf" data-testid={`gallery-shelf-${shelf.section.toLowerCase()}`}>
            <h3 className="ez-automation-shelf__title">
              {shelf.section}
              <span className="ez-automation-shelf__count">{` · ${String(shelf.templates.length)} templates`}</span>
            </h3>
            <p className="ez-automation-shelf__blurb">{shelf.blurb}</p>
            <div className="ez-automation-shelf__cards">
              {shelf.templates.map((template) => (
                <TemplateCard key={`${shelf.section}-${template.recipeId}`} template={template} onUse={onUseTemplate} />
              ))}
            </div>
          </section>
        ))}

        {shelves.length === 0 ? (
          <p className="ez-automation-create__results" data-testid="gallery-no-results">
            0 results found
          </p>
        ) : null}
      </div>
    </div>
  );
}

const RAIL_ICONS: Readonly<Record<string, (typeof icons)[keyof typeof icons]>> = {
  Explore: icons.pin,
  Recommended: icons.favorite,
  Productivity: icons.automation,
  Dates: icons.columnDate,
  Communication: icons.update,
  Sync: icons.duplicate,
  Integrations: icons.apps,
};

function TemplateCard({ template, onUse }: { template: AutomationTemplate; onUse(template: AutomationTemplate): void }) {
  return (
    <article className="ez-automation-template" data-testid={`gallery-template-${template.recipeId}`}>
      <span className="ez-automation-template__app" aria-hidden="true">
        <Icon icon={icons.automation} size={16} ignoreFocusStyle />
      </span>
      <p className="ez-automation-template__title">{template.title}</p>
      <button
        type="button"
        className="ez-automation-secondary"
        data-testid={`gallery-use-${template.recipeId}`}
        onClick={() => onUse(template)}
      >
        Use template
      </button>
    </article>
  );
}
